import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/store/authStore';
import {
  parseLineCallbackParams,
  validateLineState,
  clearLineState,
} from '@/lib/api/controlPlaneClient';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePageTranslation } from '@/i18n/useTranslation';
import { authTranslations } from '@/i18n/locales/en/auth';
import { authTranslations as thAuthTranslations } from '@/i18n/locales/th/auth';

export function LineCallbackPage() {
  const t = usePageTranslation({
    en: authTranslations,
    th: thAuthTranslations,
  });

  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(true);
  const navigate = useNavigate();
  const loginViaLine = useAuthStore((state) => state.loginViaLine);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);

  useEffect(() => {
    // G6: Wait for hydration before processing
    if (!hasHydrated) return;

    const params = parseLineCallbackParams(window.location.search);

    // G5: Handle missing params
    if (!params) {
      setError(t.lineCallbackInvalid);
      setProcessing(false);
      return;
    }

    // G5: Handle error from backend
    if (params.error) {
      setError(params.message || t.lineLoginFailed);
      setProcessing(false);
      clearLineState();  // G16: Clean up state even on error
      return;
    }

    // G15: Validate state to prevent CSRF attacks
    if (!validateLineState(params.state)) {
      setError(t.lineStateInvalid);
      setProcessing(false);
      clearLineState();
      return;
    }

    // G3: Store tokens first, then navigate
    try {
      loginViaLine(params);

      // G16: Clean up state after successful login
      clearLineState();

      // G11: Clear URL params to prevent re-execution on back button
      window.history.replaceState({}, '', '/auth/line/callback');

      // Navigate to intended destination
      const redirectTo = params.redirect || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(t.lineLoginFailed);
      setProcessing(false);
      clearLineState();
    }
  }, [hasHydrated, loginViaLine, navigate, t]);

  // Show loading while processing
  if (processing && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">{t.lineLoginProcessing}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show error state
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-destructive flex items-center gap-2">
            <AlertCircle className="h-6 w-6" />
            {t.lineLoginFailed}
          </CardTitle>
          <CardDescription>
            {error}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full"
          >
            {t.backToLogin}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
